/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { ReactNode } from 'react';
import { colors, borderRadius, shadows, transitions } from '../../styles/theme';

interface CardProps {
  children: ReactNode;
  variant?: 'default' | 'elevated' | 'outlined';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
  onClick?: () => void;
  className?: string;
}

const baseStyles = css`
  background: ${colors.background.primary};
  border-radius: ${borderRadius.xl};
  overflow: hidden;
  transition: all ${transitions.normal};
`;

const variantStyles = {
  default: css`
    box-shadow: ${shadows.sm};
  `,
  elevated: css`
    box-shadow: ${shadows.lg};
  `,
  outlined: css`
    border: 1px solid ${colors.neutral[200]};
  `,
};

const paddingStyles = {
  none: css``,
  sm: css`padding: 1rem;`,
  md: css`padding: 1.5rem;`,
  lg: css`padding: 2rem;`,
};

const hoverStyles = css`
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${shadows.xl};
  }
`;

export default function Card({
  children,
  variant = 'default',
  padding = 'md',
  hover = false,
  onClick,
  className,
}: CardProps) {
  return (
    <div
      css={[
        baseStyles,
        variantStyles[variant],
        paddingStyles[padding],
        (hover || onClick) && hoverStyles,
      ]}
      onClick={onClick}
      className={className}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {children}
    </div>
  );
}
